import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Header from '../../common/Header'
import { VIEWS } from './views'
import { format } from 'date-fns'

import { viewContext } from '../../context/viewContext'
import usePagination from './../../../../domeui/table/hooks/usePagination'

import { sortedMessages, messageThreadsByOwnerId, selectThreadById, selectMessageAuthors, selectRoles, selectParticipants, selectOwner, selectUnreadMessageCount } from '../api/apiSlice'
import { userContext } from '../../../shared/context/userContext'
import { useDisplayParticipants } from '../../common/useDisplayParticipants'
import { usePaginate } from '../../common/usePaginate'
import { startCase } from 'lodash'
import axios from 'axios'
import { setMessageThreads } from '../messageThreads/messageThreadSlice'
import { setOwners, setPagination } from '../owners/ownerSlice'
import { setMessages } from '../messages/messageSlice'
import { setUsers } from '../users/userSlice'
import { fetchInitialLoad } from '../owners/fetchInitialLoad'
import { useFetchInitialLoad } from '../../common/useFetchInitialLoad'

const NewButton = () => {
  const { setCurrentView } = useContext(viewContext)
  const handleClick = () => setCurrentView(VIEWS.NEW_MESSAGE_VIEW)

  return (
    <button onClick={handleClick} className='dome-btn dome-btn-base dome-btn-aqua-stroke'>
      <div className='dome-d-flex dome-align-center dome-gap3'>
        <i className='fal fa-pen-to-square' />
        <span>new message</span>
      </div>
    </button>
  )
}

const Thread = ({ threadId, setDrawerOpen, setActiveThreadId, isRead, page }) => {
  const { owner_id, unique_owner_id, threadsPerPage, focusId } = useContext(viewContext)

  const users = useSelector(state => state.users)
  const thread = useSelector(state => selectThreadById(state, threadId))
  const messageAuthors = useSelector(state => selectMessageAuthors(state, threadId))
  const roles = useSelector(state => selectRoles(state, owner_id))
  const allMessages = useSelector(state => state.messages.messageThreadPreviews)
  const messagesList = useSelector(state => sortedMessages(state, threadId))
  const participantList = useSelector(state => selectParticipants(state, threadId))
  const owner = useSelector(state => selectOwner(state, unique_owner_id))
  const firstMessages = useSelector(state => state.messages.first_messages)

  const firstMessage = firstMessages[thread?.first_message_id]
  const lastMessage = allMessages[thread?.last_message_id]
  const lastUserPosted = owner?.users?.[lastMessage?.posted_by]
  const unreadCount = thread.unseen_count

  const messageAuthorType = messageAuthors[lastMessage?.posted_by]
  const roleDisplay = roles[messageAuthorType]?.display_singular || messageAuthorType

  const totalMessages = thread.total_messages
  const replyCount = totalMessages ? totalMessages - 1 : 0
  const replyCountCopy = replyCount == 0 ? 'no replies yet' : `${replyCount} repl${replyCount > 1 ? 'ies' : 'y'}`

  const date = new Date(lastMessage?.posted_at || new Date().toISOString())
  const lastTimestamp = format(date, 'hh:mm aaa')
  const lastDate = format(date, 'MM/dd/yy')

  const threadClasses = ['thread-container', 'dome-d-flex', 'dome-flex-column', 'dome-gap6']
  // threadClasses.push(isRead ? 'dome-bg-white' : 'dome-bg-fill')

  const handleClick = () => {
    setDrawerOpen(true)
    setActiveThreadId(threadId)
  }

  const pseudoLink = useMemo(() => {
    if (thread.can_post) {
      return <>
        <i className='fal fa-reply' />
        <span>reply</span>
      </>
    }

    return <span>{`open >`}</span>
  }, [thread])



  const participants = useDisplayParticipants(participantList, owner)
  const transformedParticipants = participants.map(p => startCase(p.label)).join(', ')

  if (!messagesList) return

  return (
    <div onClick={handleClick} className={threadClasses.join(' ')}>
      <div className="thread-header dome-d-flex dome-justify-between dome-align-center">
        <div className="dome-d-flex dome-align-center dome-gap12">
          <div className="dome-p2 dome-text-w500 dome-d-flex dome-align-baseline dome-gap3">
            <span className="last-user-display dome-overflow-ellipsis">{lastUserPosted?.name}</span>
            <span className="role-display dome-color-med-grey">{roleDisplay}</span>
          </div>
          {unreadCount != 0 && <div className="dome-color-aqua pill-badge">{unreadCount}</div>}
        </div>
        <div className="thread-participants dome-overflow-ellipsis dome-p3">{transformedParticipants}</div>
      </div>
      <div className="thread-body">
        {lastMessage?.body !== firstMessage?.body ? (
          <>
            <div className="recent-message dome-p1-lt">{lastMessage?.body}</div>
            <div className="original-message dome-p3 dome-color-lite-grey dome-overflow-ellipsis">{firstMessage?.body}</div>
          </>
        ) : (
          <div className="recent-message dome-p1-lt">{lastMessage?.body}</div>
        )}
      </div>
      <div className="thread-footer">
        <div className="dome-d-flex dome-justify-between dome-align-center">
          <div className="dome-d-flex dome-align-center dome-gap18 dome-p3">
            <div>{lastDate}</div>
            <div>{lastTimestamp}</div>
            <div>{replyCountCopy}</div>
          </div>
          <div className="reply-pseudo-link dome-d-flex dome-align-center dome-gap3">{pseudoLink}</div>
        </div>
      </div>
    </div>
  )
}

const NoThreads = ({ isFetching }) => {
  return (
    <div style={{opacity: isFetching ? '.5' : '1'}}>
      <div className='thread-container no-messages' style={{paddingBlock: 0, paddingLeft: 0}}>
        <div className='dome-p1-lt'>no messages - create a new message using the button above.</div>
      </div>
    </div>
  )
}


const ThreadList = ({ threadsList, setActiveThreadId, setDrawerOpen, isFetching, page }) => {
  const { threadsPerPage } = useContext(viewContext)
  const containerStyle = {
    opacity: isFetching ? 0.5 : 1,
    pointerEvents: isFetching ? 'none' : 'auto'
  }

  if (!threadsList || threadsList && !threadsList.length) {
    return <NoThreads isFetching={isFetching} />
  }

  return (
    <div style={containerStyle}>
      {threadsList.map((thread, index) => {
        const isRead = thread.last_message_id == thread.last_seen_id
        return (
          <Thread
            key={index}
            threadId={thread.id}
            setActiveThreadId={setActiveThreadId}
            setDrawerOpen={setDrawerOpen}
            isRead={isRead}
            page={page}
          />
        )
      })}

    </div>
  )
}

const ThreadListContainer = ({ setActiveThreadId, setDrawerOpen, page, setThreadPage, setTotal, PaginationComponent, setMessagesPage, setOpenMessenger }) => {
  const dispatch = useDispatch()
  const { owner_id, unique_owner_id, threadsPerPage, focusId } = useContext(viewContext)
  const { isFetching, isLoading } = useFetchInitialLoad({ unique_owner_id })
  const threadsList = useSelector(state => messageThreadsByOwnerId(state, owner_id))
  const owner = useSelector(state => selectOwner(state, owner_id))
  const message_threads = useSelector(state => state.messageThreads)
  const pagination = owner?.pagination

  useEffect(() => {
    const thread = message_threads?.[focusId]

    if (!thread || !owner || owner.id !== thread.owner_id) return

    setOpenMessenger(true);
    setThreadPage(pagination.current_page)
    setDrawerOpen(true)
    setActiveThreadId(Number(focusId))
  }, [message_threads, focusId, pagination, owner])

  useEffect(() => {
    if (pagination) {
      setTotal(pagination.total_count)
    }
  }, [pagination])

  if (isLoading) {
    return <div style={{ paddingInline: '12px' }}>fetching messages... </div>
  }

  return (
    <>
      <ThreadList
        threadsList={threadsList}
        setActiveThreadId={setActiveThreadId}
        setDrawerOpen={setDrawerOpen}
        isFetching={isFetching}
        page={page}
      />
      {pagination && pagination.total_count > threadsPerPage && (
        <PaginationComponent />
      )}
    </>
  )
}

const ListView = ({ setActiveThreadId, drawerOpen, setDrawerOpen, page, setThreadPage, setTotal, PaginationComponent, setMessagesPage, setOpenMessenger }) => {
  const dispatch = useDispatch()
  const { currentView, unique_owner_id, threadsPerPage, focusId } = useContext(viewContext)
  const { fetch } = useFetchInitialLoad({ unique_owner_id, page, per_page: threadsPerPage, thread_id: focusId })

  useEffect(() => {
    fetch()
  }, [unique_owner_id, page, focusId])

  if (currentView !== VIEWS.LIST_VIEW) return

  return (
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <Header headerTitle="messaging">
        <NewButton />
      </Header>
      <ThreadListContainer
        setActiveThreadId={setActiveThreadId}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        page={page}
        setThreadPage={setThreadPage}
        setTotal={setTotal}
        PaginationComponent={PaginationComponent}
        setMessagesPage={setMessagesPage}
        setOpenMessenger={setOpenMessenger}
      />
    </div>
  )
}

export default ListView
